import React, { useState, useRef, useEffect } from 'react';
import { logEvent } from '@amplitude/analytics-browser';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Drawer from '@mui/material/Drawer';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

import PdfViewer from '../../componentsv2/PDFViewer';

import { getPdfFile } from '../../services/application';
import { getCompanyMPSAChatResponse, ChatHistoryMessage } from '../../services/company';
import { useSnackbar } from '../../context';
import { useTranslation } from '../../hooks/i18n';
import { servicingEvents } from '../../constants/events';
import { Nullable } from '../../types';
import { COLORS } from '../../themev2';

const StyledDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': {
    width: '100%',
    height: '90vh',
    left: 0,
    padding: '16px',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    overflowY: 'hidden',
  },
}));

const ChatMessage = styled(Box)<{ isUser: boolean }>(({ isUser }) => ({
  backgroundColor: isUser ? COLORS.gray[600] : COLORS.secondary.galacticGradient,
  color: isUser ? COLORS.brand.night : COLORS.brand.white,
  borderRadius: '12px',
  padding: '12px 16px',
  marginBottom: '12px',
  maxWidth: '80%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  wordBreak: 'break-word',
  // Adding for markdown formatting
  '& p': {
    margin: '8px 0',
  },
  '& strong': {
    fontWeight: 'bold',
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& code': {
    fontFamily: 'monospace',
    backgroundColor: isUser ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    padding: '2px 4px',
    borderRadius: '4px',
  },
  '& pre': {
    fontFamily: 'monospace',
    backgroundColor: isUser ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    padding: '8px',
    borderRadius: '4px',
    overflowX: 'auto',
  },
  '& ul, & ol': {
    paddingLeft: '20px',
  },
  '& li': {
    margin: '4px 0',
  },
  '& blockquote': {
    borderLeft: `4px solid ${isUser ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)'}`,
    paddingLeft: '8px',
    margin: '8px 0',
  },
  '& a': {
    color: isUser ? COLORS.secondary.indigo : COLORS.brand.white,
    textDecoration: 'underline',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 'bold',
    margin: '12px 0 8px 0',
  },
  '& h1': { fontSize: '1.5em' },
  '& h2': { fontSize: '1.3em' },
  '& h3': { fontSize: '1.1em' },
}));

const ChatContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowY: 'hidden',
});

const MessagesContainer = styled(Box)({
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  marginBottom: '16px',
});

// Styled component for typing indicator
const StyledTypingIndicator = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  padding: '8px 12px',
  marginBottom: '12px',
});

// Animated dots styling
const AnimatedDot = styled('span')({
  fontSize: '24px',
  color: COLORS.secondary.indigo,
  marginRight: '2px',
});

interface Message {
  content: string;
  isUser: boolean;
}

interface MPSAChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  documents: any[];
  sidebarOpen: boolean;
  companyId?: number;
}

// Function to sanitize input to prevent XSS
const sanitizeInput = (text: string): string => {
  // Replace potentially dangerous characters with their HTML entities
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Function to parse markdown and convert to HTML
const parseMarkdown = (text: string): string => {
  // First sanitize the input
  let formattedText = sanitizeInput(text);

  // Handle line breaks
  formattedText = formattedText.replace(/\n/g, '<br>');

  // Headers: # Heading 1, ## Heading 2, etc.
  formattedText = formattedText.replace(/<br>(\s*)#{1}\s+(.*?)(?=<br>|$)/g, '<br><h1>$2</h1>');
  formattedText = formattedText.replace(/<br>(\s*)#{2}\s+(.*?)(?=<br>|$)/g, '<br><h2>$2</h2>');
  formattedText = formattedText.replace(/<br>(\s*)#{3}\s+(.*?)(?=<br>|$)/g, '<br><h3>$2</h3>');

  // Bold: **text** or __text__
  formattedText = formattedText.replace(/\*\*(.*?)\*\*|__(.*?)__/g, '<strong>$1$2</strong>');

  // Italic: *text* or _text_
  formattedText = formattedText.replace(/\*(.*?)\*|_(.*?)_/g, '<em>$1$2</em>');

  // Links: [text](url)
  formattedText = formattedText.replace(
    /\[(.*?)\]\((.*?)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Code blocks: ```code```
  formattedText = formattedText.replace(/```([\s\S]*?)```/g, '<pre>$1</pre>');

  // Inline code: `code`
  formattedText = formattedText.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Convert consecutive list items into proper HTML lists
  // First, identify list blocks (consecutive lines starting with - or *)
  let inList = false;
  const lines = formattedText.split('<br>');
  const processedLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const listItemMatch = line.match(/^\s*[-*]\s+(.*)/);

    if (listItemMatch) {
      if (!inList) {
        inList = true;
        processedLines.push('<ul>');
      }
      processedLines.push(`<li>${listItemMatch[1]}</li>`);
    } else {
      if (inList) {
        inList = false;
        processedLines.push('</ul>');
      }
      processedLines.push(line);
    }
  }

  if (inList) {
    processedLines.push('</ul>');
  }

  formattedText = processedLines.join('<br>');

  // Blockquotes: > quote
  formattedText = formattedText.replace(/<br>(\s*>+\s+)(.*?)(?=<br>|$)/g, '<br><blockquote>$2</blockquote>');

  // Clean up: Fix any issues with nested <br> tags inside lists or other HTML elements
  formattedText = formattedText.replace(/<(ul|ol|pre|blockquote)><br>/g, '<$1>');
  formattedText = formattedText.replace(/<br><\/(ul|ol|pre|blockquote)>/g, '</$1>');

  return formattedText;
};

// Message component that renders markdown
const MarkdownMessage: React.FC<{ content: string; isUser: boolean }> = ({ content, isUser }) => {
  return <ChatMessage isUser={isUser} dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }} />;
};

// Animated typing indicator component
const TypingIndicator: React.FC = () => {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prevCount) => (prevCount % 3) + 1);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const dots = '.'.repeat(dotCount);

  return (
    <StyledTypingIndicator>
      <AnimatedDot>{dots}</AnimatedDot>
    </StyledTypingIndicator>
  );
};

const MPSAChatDrawer: React.FC<MPSAChatDrawerProps> = ({ isOpen, onClose, documents, companyId }) => {
  const { i18n } = useTranslation('mpsa_chat');
  const [message, setMessage] = useState<string>('');

  // Create direct translation references for MPSA chat UI
  const [drawerTranslations, setDrawerTranslations] = useState({
    drawerTitle: i18n.language.startsWith('es')
      ? 'Comprende tu Contrato con IA'
      : 'Understand Your Contract with AI',
    initialGreeting: i18n.language.startsWith('es')
      ? '¡Hola! Soy tu asistente de IA. Puedo ayudarte a entender tu contrato MPSA. ¿Qué te gustaría saber?'
      : "Hi! I'm your AI assistant. I can help you understand your MPSA contract. What would you like to know?",
    mpsa_document: i18n.language.startsWith('es') ? 'Documento MPSA' : 'MPSA Document',
    no_document: i18n.language.startsWith('es') ? 'No hay documento disponible' : 'No document available',
    placeholder: i18n.language.startsWith('es')
      ? 'Haz una pregunta sobre el MPSA...'
      : 'Ask a question about the MPSA...',
    error_processing: i18n.language.startsWith('es')
      ? 'Lo siento, no pude procesar tu pregunta en este momento.'
      : "I'm sorry, I couldn't process your question at this time.",
    error_service: i18n.language.startsWith('es')
      ? 'Lo siento, hubo un error al procesar tu solicitud. Por favor, inténtalo de nuevo más tarde.'
      : "I'm sorry, there was an error processing your request. Please try again later.",
    error_snackbar: i18n.language.startsWith('es')
      ? 'Error al comunicarse con el servicio de IA'
      : 'Error communicating with the AI service',
    disclaimer: i18n.language.startsWith('es')
      ? 'Esta herramienta de IA proporciona únicamente información general y no constituye asesoramiento legal. Las respuestas pueden ser incompletas o inexactas. Consulte con profesionales legales calificados. Al utilizar esta herramienta, reconoce estas limitaciones.'
      : 'This AI tool provides general information only and not legal advice. Answers may be incomplete or inaccurate. Consult qualified legal professionals. By using this tool, you acknowledge these limitations.',
  });

  // Update translations when language changes
  useEffect(() => {
    setDrawerTranslations({
      drawerTitle: i18n.language.startsWith('es')
        ? 'Comprende tu Contrato con IA'
        : 'Understand Your Contract with AI',
      initialGreeting: i18n.language.startsWith('es')
        ? '¡Hola! Soy tu asistente de IA. Puedo ayudarte a entender tu contrato MPSA. ¿Qué te gustaría saber?'
        : "Hi! I'm your AI assistant. I can help you understand your MPSA contract. What would you like to know?",
      mpsa_document: i18n.language.startsWith('es') ? 'Documento MPSA' : 'MPSA Document',
      no_document: i18n.language.startsWith('es') ? 'No hay documento disponible' : 'No document available',
      placeholder: i18n.language.startsWith('es')
        ? 'Haz una pregunta sobre el MPSA...'
        : 'Ask a question about the MPSA...',
      error_processing: i18n.language.startsWith('es')
        ? 'Lo siento, no pude procesar tu pregunta en este momento.'
        : "I'm sorry, I couldn't process your question at this time.",
      error_service: i18n.language.startsWith('es')
        ? 'Lo siento, hubo un error al procesar tu solicitud. Por favor, inténtalo de nuevo más tarde.'
        : "I'm sorry, there was an error processing your request. Please try again later.",
      error_snackbar: i18n.language.startsWith('es')
        ? 'Error al comunicarse con el servicio de IA'
        : 'Error communicating with the AI service',
      disclaimer: i18n.language.startsWith('es')
        ? 'Esta herramienta de IA proporciona únicamente información general y no constituye asesoramiento legal. Las respuestas pueden ser incompletas o inexactas. Consulte con profesionales legales calificados. Al utilizar esta herramienta, reconoce estas limitaciones.'
        : 'This AI tool provides general information only and not legal advice. Answers may be incomplete or inaccurate. Consult qualified legal professionals. By using this tool, you acknowledge these limitations.',
    });
  }, [i18n.language]);

  const [messages, setMessages] = useState<Message[]>([
    {
      content: drawerTranslations.initialGreeting,
      isUser: false,
    },
  ]);

  // Update state to use the imported ChatHistoryMessage type
  const [chatHistory, setChatHistory] = useState<ChatHistoryMessage[]>([]);

  // Track drawer open event
  useEffect(() => {
    if (isOpen) {
      logEvent(servicingEvents.OPEN_MPSA_CHAT_DRAWER, {
        companyId: companyId,
        timestamp: new Date().toISOString(),
      });
      setMessages([
        {
          content: drawerTranslations.initialGreeting,
          isUser: false,
        },
      ]);
      // Reset chat history when drawer is opened
      setChatHistory([]);
    }
  }, [isOpen, drawerTranslations.initialGreeting, companyId]);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<Nullable<string>>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (documents?.[0]?.uuid) {
      setIsLoadingPdf(true);
      getPdfFile(documents[0].uuid)
        .then((file) => {
          const url = URL.createObjectURL(file.data);
          setPdfUrl(url);
        })
        .catch((error) => {
          setPdfUrl(null);
        })
        .finally(() => {
          setIsLoadingPdf(false);
        });
    }
  }, [documents]);

  // Get current language
  const currentLanguage = i18n.language.substring(0, 2);

  const handleSendMessage = async () => {
    if (message.trim() === '' || !companyId) return;

    // Add user message
    const userMessage: Message = {
      content: message,
      isUser: true,
    };

    // Track message sent event - Strip long messages to avoid sending too much data
    const sanitizedMessageContent = message.length > 250 ? `${message.substring(0, 250)}...` : message;

    logEvent(servicingEvents.SEND_MPSA_CHAT_MESSAGE, {
      companyId: companyId,
      messageLength: message.length,
      message_content: sanitizedMessageContent,
      timestamp: new Date().toISOString(),
      language: currentLanguage,
    });

    setMessages((prev) => [...prev, userMessage]);
    setMessage('');
    setIsProcessing(true);

    try {
      // Prepare the query with extra contextual information
      // Add a timestamp to make each query unique even if the text is the same
      const timestamp = new Date().toISOString();
      const enhancedQuery = `QUERY [${timestamp}]: ${message.trim()}`;

      // Get response from backend API - pass the language and chat history
      console.log(
        'Sending chat request for company:',
        companyId,
        'with message:',
        message.trim(),
        'language:',
        currentLanguage,
        'chat history length:',
        chatHistory.length
      );
      console.log('DEBUG - Chat history being sent to API:', JSON.stringify(chatHistory, null, 2));

      const response = await getCompanyMPSAChatResponse(
        companyId,
        enhancedQuery, // Send the enhanced query with timestamp
        currentLanguage,
        chatHistory // Send the existing chat history, not including current message
      );
      console.log('Received chat response:', response);

      const aiResponse: Message = {
        content: response.content || drawerTranslations.error_processing,
        isUser: false,
      };

      setMessages((prev) => [...prev, aiResponse]);

      // Now update chat history with both the user message and AI response
      // Store the original message in chat history, not the enhanced one
      const updatedChatHistory: ChatHistoryMessage[] = [
        ...chatHistory,
        { role: 'user', content: message.trim() },
        { role: 'assistant', content: response.content || drawerTranslations.error_processing },
      ];

      // Limit chat history to 6 messages (3 exchanges)
      const limitedChatHistory =
        updatedChatHistory.length > 6
          ? updatedChatHistory.slice(updatedChatHistory.length - 6)
          : updatedChatHistory;

      console.log(
        'DEBUG - Updated chat history after response:',
        JSON.stringify(limitedChatHistory, null, 2)
      );
      setChatHistory(limitedChatHistory);
    } catch (error: any) {
      console.error('Error getting chat response:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });

      const errorResponse: Message = {
        content: drawerTranslations.error_service,
        isUser: false,
      };

      setMessages((prev) => [...prev, errorResponse]);
      showSnackbar(drawerTranslations.error_snackbar, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Wrap the onClose function to track closing event
  const handleClose = () => {
    logEvent(servicingEvents.CLOSE_MPSA_CHAT_DRAWER, {
      companyId: companyId,
      timestamp: new Date().toISOString(),
      messageCount: messages.filter((msg) => msg.isUser).length,
    });
    onClose();
  };

  const documentViewer = pdfUrl ? (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <PdfViewer pdfUrl={pdfUrl} />
    </Box>
  ) : (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="body1" color="text.secondary">
        {drawerTranslations.no_document}
      </Typography>
    </Box>
  );

  return (
    <StyledDrawer anchor="bottom" open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {drawerTranslations.drawerTitle}
          </Typography>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {/* PDF Viewer */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              height: { xs: '40%', sm: '45%', md: '100%' },
              overflow: 'hidden',
              order: { xs: 1, md: 1 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                border: `1px solid ${COLORS.gray[600]}`,
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: { xs: 1, sm: 2 },
                  borderBottom: `1px solid ${COLORS.gray[600]}`,
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium" noWrap>
                  {documents.length > 0
                    ? documents[0].fileName || drawerTranslations.mpsa_document
                    : drawerTranslations.no_document}
                </Typography>
              </Box>

              <Box
                ref={pdfContainerRef}
                sx={{
                  flexGrow: 1,
                  overflow: 'auto',
                  backgroundColor: COLORS.gray[300],
                  position: 'relative',
                  p: 1,
                  width: '100%',
                }}
              >
                {isLoadingPdf ? <CircularProgress size={24} /> : documentViewer}
              </Box>
            </Paper>
          </Grid>

          {/* Chat Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              height: { xs: '60%', sm: '55%', md: '100%' },
              overflow: 'hidden',
              order: { xs: 2, md: 2 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                border: `1px solid ${COLORS.gray[600]}`,
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <ChatContainer>
                <MessagesContainer>
                  {messages.map((msg, index) => (
                    <MarkdownMessage key={index} content={msg.content} isUser={msg.isUser} />
                  ))}
                  {isProcessing && <TypingIndicator />}
                  <div ref={messagesEndRef} />
                </MessagesContainer>

                <Box sx={{ p: { xs: 1, sm: 2 }, borderTop: `1px solid ${COLORS.gray[600]}` }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={drawerTranslations.placeholder}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      disabled={isProcessing}
                      size="small"
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={isProcessing || message.trim() === ''}
                      size="medium"
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </ChatContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Disclaimer at the bottom of the drawer */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: COLORS.gray[1000], fontStyle: 'italic', fontSize: { xs: '10px', sm: '12px' } }}
          >
            {drawerTranslations.disclaimer}
          </Typography>
        </Box>
      </Box>
    </StyledDrawer>
  );
};

export default MPSAChatDrawer;
